"use client";
import { useState, useEffect, useRef, useCallback, useSyncExternalStore, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

// Клиентский mount-guard для портала без setState-в-эффекте:
// на сервере → false, после гидрации на клиенте → true.
const emptySubscribe = () => () => {};
function useMounted(): boolean {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
}
import { XMarkIcon } from "@heroicons/react/24/solid";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumber } from "libphonenumber-js";
import { useTranslations } from "next-intl";
import { logEvent } from "@/app/[locale]/googleAnalytics";

const isValidPhone = (phone: string): boolean => {
  try {
    return parsePhoneNumber(phone).isValid();
  } catch {
    return false;
  }
};

type Props = {
  // header — компактная кнопка для шапки (зеркальна «Купить», но обведённая-вторичная);
  // menu — крупная во всю ширину для бургера (одинаковая с «Купить» в бургере).
  variant?: "header" | "menu";
};

/**
 * Кнопка «Аренда» в хедере/бургере + модалка с заявкой (Имя + Телефон).
 * Заявка уходит в Telegram через /api/rent (тот же бот, чат аренды, с пометкой
 * «Whale Marina»). Вторичный CTA — покупка (PurchaseModal) визуально доминирует.
 */
export default function RentModal({ variant = "header" }: Props) {
  const t = useTranslations("rent");
  const tMenu = useTranslations("menu");

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [loading, setLoading] = useState(false);

  // Стиль react-phone-input-2 (~44KB) подключаем <link>-ом ПО URL на МАУНТЕ навбара
  // (не при open!): чтобы к моменту открытия модалки CSS уже скачан и дропдаун стран
  // отрисовался сразу, а не «голым» (флаги обрезаны, текст наезжает — пока грузятся
  // 44KB). Грузим ПОСЛЕ гидрации → в SSR-<head> его нет, первый рендер страницы не
  // блокируется (import "...css" наверху Next хойстил в render-blocking head, был
  // ~40% критического CSS). Стили затрагивают только .react-tel-input, которого на
  // странице ещё нет, поэтому ранняя подгрузка не даёт визуальных side-effect'ов.
  useEffect(() => {
    const HREF = "/phone-input.css";
    if (document.querySelector(`link[href="${HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = HREF;
    document.head.appendChild(link);
  }, []);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Портал монтируем только на клиенте (SSR не знает про document).
  const mounted = useMounted();

  // close объявлен ДО focus-trap-эффекта (который его вызывает) и обёрнут в
  // useCallback — иначе eslint react-hooks/immutability ругается на «доступ до
  // объявления». Зависимостей нет: все setState-сеттеры стабильны.
  const close = useCallback(() => {
    setOpen(false);
    // сбрасываем состояние с небольшой задержкой, чтобы не мигало при закрытии
    setTimeout(() => {
      setDone(false);
      setServerError("");
      setErrors({});
      setName("");
      setPhone("");
      setComment("");
    }, 300);
  }, []);

  // Доступность модалки (a11y MANUAL: focus-traps / managed-focus). При открытии:
  // переводим фокус в диалог; Esc закрывает; Tab заперт внутри (не уходит на фон);
  // при закрытии возвращаем фокус на кнопку-триггер.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    // фокус на первый интерактивный элемент диалога
    const focusables = () =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => el.offsetParent !== null)
        : [];
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const trigger = triggerRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      // вернуть фокус на кнопку, открывшую модалку
      trigger?.focus();
    };
  }, [open, close]);

  // Блокируем скролл фона, пока модалка открыта.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    const nextErrors: { name?: string; phone?: string } = {};
    if (!name.trim()) nextErrors.name = t("errorName");
    if (!isValidPhone(formattedPhone)) nextErrors.phone = t("errorPhone");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      const res = await fetch("/api/rent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: formattedPhone,
          comment: comment.trim(),
          website,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setDone(true);
      logEvent("Заявка на аренду", {
        event_category: "Rent Form",
        event_label: "Header rent request",
      });
    } catch {
      setServerError(t("error"));
    } finally {
      setLoading(false);
    }
  };

  // Аренда — ВТОРИЧНЫЙ CTA, зеркало «Купить» той же формы/размера, отличается цветом.
  // header — форма как у старой «Contact us», но ЗЕРКАЛЬНО: округлена СПРАВА
  //   (rounded-r-full), плоский язычок СЛЕВА. Та же фикс-высота h-[46px], без переноса.
  //   Пара «Купить ◗ ◖ Аренда» смотрит в разные стороны — как просил заказчик.
  // menu — крупная во всю ширину для бургера.
  const triggerClass =
    variant === "menu"
      ? "w-full bg-[#005F70] hover:bg-[#007F90] text-white font-semibold px-4 py-[14px] rounded-full text-sm text-center"
      : "inline-flex items-center justify-center h-[46px] whitespace-nowrap bg-[#005F70] hover:bg-[#007F90] text-white text-sm font-medium px-6 rounded-r-full rounded-l-md";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
      >
        {tMenu("rentButton")}
      </button>

      {/* Портал в body: иначе fixed-позиционирование считается от навбара
          (у него position:fixed + transform от framer-motion) и модалка уезжает. */}
      {mounted &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* backdrop */}
            <button
              type="button"
              aria-label={t("close")}
              onClick={close}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />

            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-label={t("title")}
              className="relative w-full max-w-[420px] max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-6 shadow-xl"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                type="button"
                onClick={close}
                aria-label={t("close")}
                className="absolute top-4 right-4 text-[#005F70] hover:text-[#04BBDC] transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {done ? (
                <div className="py-8 text-center">
                  <p className="text-[#005F70] font-semibold text-lg">{t("success")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div>
                    <h2 className="text-[22px] font-bold text-[#005F70]">{t("title")}</h2>
                    <p className="text-sm text-gray-600 mt-1">{t("subtitle")}</p>
                  </div>

                  <div>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("name")}
                      autoComplete="name"
                      className="w-full h-12 px-4 border border-gray-300 rounded-md focus:outline-none focus:border-[#04BBDC]"
                    />
                    {errors.name && <span className="text-red-500 text-xs mt-1 block">{errors.name}</span>}
                  </div>

                  <div className="w-full min-w-0">
                    <PhoneInput
                      country={"th"}
                      value={phone}
                      onChange={setPhone}
                      inputProps={{ name: "phone", autoComplete: "tel" }}
                      containerClass="!w-full"
                      inputClass="!w-full !h-12"
                    />
                    {errors.phone && <span className="text-red-500 text-xs mt-1 block">{errors.phone}</span>}
                  </div>

                  <div>
                    <textarea
                      name="comment"
                      autoComplete="off"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t("comment")}
                      rows={3}
                      maxLength={1000}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:border-[#04BBDC]"
                    />
                  </div>

                  {/* honeypot — скрыто от людей, ловит ботов */}
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />

                  {serverError && <span className="text-red-500 text-sm">{serverError}</span>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="h-12 bg-[#005F70] hover:bg-[#007F90] disabled:opacity-60 transition-colors text-white font-medium rounded-md"
                  >
                    {loading ? t("submitting") : t("submit")}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
